import { ReactNode } from 'react'
import { PopoverMenu } from './PopoverMenu'
import { clsx } from 'clsx'
import { Tip } from './Tip'

export function AssigneeList({
  children,
  menu,
  isOpen,
  className,
  setIsOpen,
  disabled,
  ariaLabel
}: {
  children: ReactNode
  menu?: ReactNode
  isOpen?: boolean
  className?: string
  setIsOpen?: (isOpen: boolean) => void
  disabled?: boolean
  ariaLabel?: string
}) {
  if (!menu || disabled) {
    return <div className={clsx('flex flex-row items-center gap-1', className)}>{children}</div>
  }

  return (
    <PopoverMenu
      isControlled={true}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      trigger={
        <button
          type="button"
          aria-label={ariaLabel}
          className={clsx(
            'flex cursor-pointer flex-row items-center gap-1',
            // reset button styles
            'appearance-none bg-transparent p-0 text-left',
            className
          )}
        >
          {children}
        </button>
      }
      placement="bottom-start"
      offset={{ dy: 4 }}
    >
      {menu}
    </PopoverMenu>
  )
}

const ASSIGNEE_COLORS: Record<string, string> = {
  red: 'bg-red-200 text-red-700 border-red-200',
  green: 'bg-green-300 text-green-700 border-green-300',
  blue: 'bg-blue-200 text-blue-600 border-blue-200',
  yellow: 'bg-yellow-200 text-yellow-600 border-yellow-200',
  purple: 'bg-purple-200 text-purple-600 border-purple-200',
  pink: 'bg-pink-200 text-pink-600 border-pink-200',
  gray: 'bg-gray-200 text-gray-600 border-gray-200',
  outline: 'border border-gray-200 text-gray-500'
}

export type AssigneeColor = 'red' | 'green' | 'blue' | 'yellow' | 'purple' | 'pink' | 'gray'

export function AssigneeBadge({
  children,
  color,
  tip
}: {
  children: ReactNode
  color: string
  tip?: string
}) {
  return (
    <Tip title={tip}>
      <div
        className={clsx(
          'flex size-7 min-w-7 flex-row items-center justify-center rounded-full text-center text-sm',
          ASSIGNEE_COLORS[color]
        )}
      >
        {children}
      </div>
    </Tip>
  )
}

export function AssigneeNumberBadge({ children }: { children: ReactNode }) {
  return (
    <div className="flex size-7 flex-row items-center justify-center rounded-full border border-gray-200 text-center text-sm text-gray-500">
      {children}
    </div>
  )
}

export function AssignIcon({ className }: { className: string }) {
  return (
    <div className="flex size-7 flex-row items-center justify-center rounded-full text-center text-sm text-gray-500">
      <svg className={clsx('size-5', className)} viewBox="0 0 16 16">
        <path
          fill="currentColor"
          d="M16 8.5a.5.5 0 0 1-.5.5h-1v1a.5.5 0 1 1-1 0V9h-1a.5.5 0 0 1 0-1h1V7a.5.5 0 0 1 1 0v1h1a.5.5 0 0 1 .5.5m-3.62 3.68a.5.5 0 1 1-.76.64 6.3 6.3 0 0 0-9.74 0 .5.5 0 1 1-.76-.64A7.4 7.4 0 0 1 4.5 9.85a4.25 4.25 0 1 1 4.5 0 7.5 7.5 0 0 1 3.38 2.33M6.75 9.5a3.25 3.25 0 1 0 0-6.5 3.25 3.25 0 0 0 0 6.5"
        />
      </svg>
    </div>
  )
}
